/* Permissions — the client's own left-menu item, beside Groups.
 *
 * From page 2 of their Groups&Subgroups document, which draws two tabs:
 *
 *   MODERATORS — Add opens Name / Mobile / Email / Role / Save, and their
 *   annotation reads "(Auto populate) from group list members". That matters:
 *   a moderator is CHOSEN from people already in the church, not typed in
 *   fresh. So this is a picker over the member list, not a form.
 *
 *   ROLES — Add opens Name ("Moderator-1" is their example), Description, and
 *   a checkbox list of permissions.
 *
 * THE PERMISSION LIST COMES FROM THE API. Their document names one permission,
 * "Viewing other members", and annotates the rest "As used in slack" — so the
 * vocabulary is ours to propose. It is served from /groups/permission-catalogue
 * rather than hard-coded here, so this screen and the validator that rejects
 * unknown permissions can never disagree about what exists.
 */
import { useMemo, useState } from 'react'
import { Check, Loader2, Pencil, Plus, Shield, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Confirm } from '@/components/ui/confirm'
import { Field } from '@/components/ui/field'
import { Modal } from '@/components/ui/modal'
import { useChurch, useCreateRole, useDeleteRole, useUpdateRole, useMe, usePermissionCatalogue, useRoles } from '@/api/queries'
import { cn } from '@/lib/utils'
import type { ChurchRole } from '@/types/api'


export function PermissionsPage() {
  const { data: me } = useMe()
  const churchId = me?.church_id ?? null
  const church = useChurch(churchId)

  if (!me) return null

  if (!churchId) {
    return (
      <Card className="p-s8 text-center">
        <Shield className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
        <p className="mt-s3 text-md font-bold">No church to administer</p>
        <p className="mt-1 text-base text-ink-2">
          Permissions belong to a church.
        </p>
      </Card>
    )
  }

  return (
    <div className="space-y-s4">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Permissions</h1>
        <p className="mt-1 text-base text-ink-2">{church.data?.name ?? '…'}</p>
      </header>

      <Roles churchId={churchId} />
    </div>
  )
}

/* ------------------------------------------------------------------ roles */

function Roles({ churchId }: { churchId: string }) {
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState<ChurchRole | null>(null)
  const [deleting, setDeleting] = useState<ChurchRole | null>(null)
  const roles = useRoles(churchId)
  const remove = useDeleteRole(churchId)
  const catalogue = usePermissionCatalogue()

  // Key → plain-English label, so a role's stored permissions can be shown in
  // the same words the checkboxes used.
  const labels = useMemo(() => {
    const map = new Map<string, string>()
    catalogue.data?.forEach((section) =>
      section.permissions.forEach((p: { key: string; label: string }) =>
        map.set(p.key, p.label),
      ),
    )
    return map
  }, [catalogue.data])

  return (
    <div className="space-y-s3">
      <div className="flex justify-end">
        <Button variant="primary" onClick={() => setAdding(true)}>
          <Plus className="h-4 w-4" aria-hidden />
          Add role
        </Button>
      </div>

      {roles.isLoading ? (
        <div className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </div>
      ) : null}

      {!roles.isLoading && !roles.data?.length ? (
        <Card className="p-s8 text-center">
          <Shield className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
          <p className="mt-s3 text-md font-bold">No roles yet</p>
          <p className="mt-1 text-base text-ink-2">
            A role is a named set of things somebody may do.
          </p>
        </Card>
      ) : null}

      {roles.data?.map((r) => (
        <Card key={r.id} className="p-s4">
          <div className="flex items-start gap-s3">
            <div className="min-w-0 flex-1">
              <h3 className="text-md font-bold">{r.name}</h3>
              {r.description ? (
                <p className="mt-1 text-base text-ink-2">{r.description}</p>
              ) : null}
            </div>
            <Button variant="secondary" size="sm" onClick={() => setEditing(r)}>
              <Pencil className="h-4 w-4" aria-hidden />
              Edit
            </Button>
            <button
              onClick={() => setDeleting(r)}
              aria-label={`Delete the ${r.name} role`}
              className="grid h-8 w-8 shrink-0 place-items-center rounded-pill text-ink-2 transition-colors hover:bg-hover-2 hover:text-danger"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </button>
          </div>
          {r.permissions.length ? (
            <ul className="mt-s3 flex flex-wrap gap-s2">
              {r.permissions.map((p) => (
                <li
                  key={p}
                  className="rounded-pill bg-surface-2 px-s3 py-1 text-sm font-semibold text-ink-2"
                >
                  {labels.get(p) ?? p}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-s2 text-base text-ink-2">
              No permissions — this role can do nothing yet.
            </p>
          )}
        </Card>
      ))}

      {adding ? (
        <RoleModal churchId={churchId} onClose={() => setAdding(false)} />
      ) : null}

      {editing ? (
        <RoleModal
          churchId={churchId}
          role={editing}
          onClose={() => setEditing(null)}
        />
      ) : null}

      <Confirm
        open={Boolean(deleting)}
        onOpenChange={(next) => !next && setDeleting(null)}
        title={deleting ? `Delete the ${deleting.name} role?` : ''}
        body="Anyone carrying it stays a moderator, but loses this role and everything it allowed."
        confirmLabel="Delete role"
        loading={remove.isPending}
        onConfirm={() =>
          deleting &&
          remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
        }
      />
    </div>
  )
}

/** One form for creating and for editing.
 *
 *  Two modals would drift: a permission added to one and forgotten in the
 *  other is exactly the kind of gap nobody notices until a role cannot be
 *  given something it could be created with. `role` present means edit. */
function RoleModal({
  churchId,
  role,
  onClose,
}: {
  churchId: string
  role?: ChurchRole
  onClose: () => void
}) {
  const editing = Boolean(role)
  const [name, setName] = useState(role?.name ?? '')
  const [description, setDescription] = useState(role?.description ?? '')
  const [selected, setSelected] = useState<string[]>(role?.permissions ?? [])
  const catalogue = usePermissionCatalogue()
  const create = useCreateRole(churchId)
  const update = useUpdateRole(churchId)
  const saving = create.isPending || update.isPending
  const failed = create.isError || update.isError

  function toggle(key: string) {
    setSelected((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    )
  }

  function submit() {
    if (!name.trim() || saving) return
    const body = {
      name: name.trim(),
      description: description.trim() || null,
      permissions: selected,
    }
    if (role) {
      update.mutate({ roleId: role.id, ...body }, { onSuccess: onClose })
    } else {
      create.mutate(body, { onSuccess: onClose })
    }
  }

  return (
    <Modal
      open
      onOpenChange={(next) => !next && onClose()}
      title={editing ? `Edit ${role!.name}` : 'New role'}
      width="560px"
      guardClose={Boolean(name.trim() || selected.length)}
      footer={
        <Button
          variant="primary"
          size="lg"
          full
          onClick={submit}
          disabled={!name.trim()}
          loading={saving}
        >
          {editing ? 'Save changes' : 'Create role'}
        </Button>
      }
    >
      <div className="space-y-s3 p-s4">
        <Field
          id="role-name"
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Moderator-1"
          autoFocus
          required
        />
        <Field
          id="role-description"
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Looks after the youth group's posts"
        />

        {catalogue.isLoading ? (
          <div className="flex justify-center py-s5">
            <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
          </div>
        ) : null}

        {catalogue.data?.map((section) => (
          <div key={section.section}>
            <h3 className="pb-s1 pt-s2 text-sm font-bold uppercase tracking-wide text-ink-2">
              {section.section}
            </h3>
            <ul className="space-y-s1">
              {section.permissions.map(({ key, label }) => {
                const on = selected.includes(key)
                return (
                  <li key={key}>
                    <button
                      onClick={() => toggle(key)}
                      aria-pressed={on}
                      className={cn(
                        'flex w-full items-center gap-s3 rounded-md border px-s3 py-s2 text-left text-base font-semibold transition-colors',
                        on
                          ? 'border-brand bg-brand-softer text-brand'
                          : 'border-line text-ink hover:bg-hover',
                      )}
                    >
                      <span
                        className={cn(
                          'grid h-5 w-5 shrink-0 place-items-center rounded-sm border',
                          on ? 'border-brand bg-brand text-white' : 'border-line',
                        )}
                        aria-hidden
                      >
                        {on ? <Check className="h-3.5 w-3.5" /> : null}
                      </span>
                      {label}
                    </button>
                  </li>
                )
              })}
            </ul>
          </div>
        ))}

        {failed ? (
          <p className="text-base text-danger">
            That did not save. A role with that name may already exist.
          </p>
        ) : null}
      </div>
    </Modal>
  )
}
